import React, { useRef, useState, useEffect } from 'react';

interface VideoPlayerProps {
  src?: string;
  sources?: Array<{src: string, type: string}>;
  poster?: string;
  className?: string;
  aspectRatio?: "landscape" | "portrait";
  preload?: "none" | "metadata" | "auto";
  playbackRate?: number;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  sources,
  poster,
  className = "",
  aspectRatio = "landscape",
  preload = "metadata",
  playbackRate = 1,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(0.3);
  const [showControls, setShowControls] = useState(true);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const isInView = true; // Simplified version without the hook
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const volumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Set video duration once metadata is loaded
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setLoading(false);

      // Set playback rate
      video.playbackRate = playbackRate;
      
      // Set initial volume
      video.volume = volume;

      // If in view and hasn't been interacted with, autoplay when visible
      if (isInView && !hasInteracted) {
        video.play().catch(() => {
          // Autoplay might be prevented by browser policy, that's okay
        });
      }
    };

    // Additional handler to ensure loading state is updated when playback actually starts
    const handlePlaying = () => {
      setLoading(false);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('playing', handlePlaying);
    
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('playing', handlePlaying);
    };
  }, [isInView, hasInteracted, playbackRate, volume]);

  // Set up event listeners for playback state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      // With loop attribute, this won't trigger unless there's an error
      setIsPlaying(false);
    };
    
    // Add handler for when playback stalls/waiting
    const handleWaiting = () => setLoading(true);
    const handleCanPlay = () => setLoading(false);

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, []);

  // Handle in-view visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video || hasInteracted) return;

    if (isInView) {
      // Try to play when in view (if it hasn't been interacted with)
      video.play().catch(() => {
        // Autoplay might be prevented by browser policy
      });
    } else if (isPlaying) {
      // Pause when out of view
      video.pause();
    }
  }, [isInView, isPlaying, hasInteracted]);

  // Show/hide controls with timeout
  useEffect(() => {
    // Clear any existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    if (isPlaying && showControls) {
      // Hide controls after 2.5 seconds when playing
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2500);
    }

    // Cleanup on unmount
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying, showControls]);

  // Cleanup volume timeout on unmount
  useEffect(() => {
    return () => {
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current);
      }
    };
  }, []);

  // Handle user interaction with container
  const handleContainerInteraction = () => {
    setShowControls(true);

    // Reset the timer
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    // Start a new timer if video is playing
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2500);
    }
  };

  // Toggle play/pause
  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    
    setHasInteracted(true);

    if (isPlaying) {
      video.pause();
    } else {
      setLoading(true); // Show loading when attempting to play
      video.play().catch(error => {
        console.error("Error playing video:", error);
        setLoading(false); // Hide loading if play fails
      });
    }
  };

  // Toggle mute
  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    setHasInteracted(true);
    setIsMuted(!isMuted);
    video.muted = !isMuted;
  };

  // Handle volume control hover
  const handleVolumeMouseEnter = () => {
    if (volumeTimeoutRef.current) {
      clearTimeout(volumeTimeoutRef.current);
    }
    setShowVolumeSlider(true);
  };

  const handleVolumeMouseLeave = () => {
    volumeTimeoutRef.current = setTimeout(() => {
      setShowVolumeSlider(false);
    }, 500); // Delay hiding to allow movement between speaker and slider
  };

  // Update volume
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation(); // Prevent event from bubbling up to container
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    video.volume = newVolume;
    
    if (newVolume === 0) {
      setIsMuted(true);
      video.muted = true;
    } else if (isMuted) {
      setIsMuted(false);
      video.muted = false;
    }
  };

  // Prevent slider click from triggering play/pause
  const handleVolumeSliderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      ref={containerRef}
      className={`relative group overflow-hidden ${
        aspectRatio === "landscape" ? "aspect-video" : "aspect-[9/16]"
      } ${className}`}
      onMouseMove={handleContainerInteraction}
      onMouseEnter={() => setShowControls(true)}
      onClick={togglePlay}
    >
      <div className="absolute inset-0 bg-black">
        <video
          ref={videoRef}
          preload={preload}
          playsInline
          muted={isMuted}
          loop
          className="w-full h-full object-cover"
          poster={poster}
        >
          {sources && sources.length > 0 ? (
            sources.map((source, index) => (
              <source key={index} src={source.src} type={source.type} />
            ))
          ) : (
            src && <source src={src} type={src.endsWith('.mp4') ? 'video/mp4' : src.endsWith('.webm') ? 'video/webm' : ''} />
          )}
          Your browser does not support HTML5 video.
        </video>
      </div>

      {/* Loading spinner - only show when loading is true and not playing */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-10">
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
        </div>
      )}

      {/* Minimal play button when paused */}
      {!isPlaying && !loading && (
        <div
          className="absolute inset-0 flex items-center justify-center z-10"
          onClick={togglePlay}
        >
          <div className="p-3 rounded-full bg-black/30 backdrop-blur-sm">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 text-white">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}
      
      {/* Minimal controls - play/pause button (conditional) and always visible speaker */}
      <div className="absolute inset-x-0 bottom-0 flex justify-between items-end p-3 z-20 pointer-events-none">
        {/* Play/Pause button - only visible when controls are shown */}
        <div className={`transition-opacity duration-300 pointer-events-auto ${
          (showControls || !isPlaying) ? 'opacity-100' : 'opacity-0'
        }`}>
          <button
            onClick={togglePlay}
            className="text-white opacity-80 hover:opacity-100 focus:outline-none transition-opacity"
            aria-label={isPlaying ? "Pause" : "Play"}
            type="button"
          >
            {isPlaying ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7 0a.75.75 0 01.75-.75h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75h-1.5a.75.75 0 01-.75-.75V5.25z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>

        {/* Volume control - speaker always visible, slider appears on hover to the left */}
        <div 
          className="relative flex items-center pointer-events-auto"
          onMouseEnter={handleVolumeMouseEnter}
          onMouseLeave={handleVolumeMouseLeave}
        >
          {/* Volume slider - appears on hover to the left of speaker */}
          <div 
            className={`absolute right-full mr-2 transition-all duration-200 ${
              showVolumeSlider 
                ? 'opacity-100 translate-x-0 visible' 
                : 'opacity-0 translate-x-2 invisible'
            }`}
            onClick={handleVolumeSliderClick}
          >
            <div className="flex items-center bg-black/60 backdrop-blur-sm px-3 py-2 rounded-full">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                onClick={handleVolumeSliderClick}
                className="w-20 h-1.5 accent-white cursor-pointer"
                aria-label="Volume"
              />
            </div>
          </div>
          
          {/* Speaker button - always visible */}
          <button
            onClick={toggleMute}
            className="text-white bg-black/40 backdrop-blur-sm p-2 rounded-full opacity-80 hover:opacity-100 focus:outline-none transition-opacity"
            aria-label={isMuted ? "Unmute" : "Mute"}
            type="button"
          >
            {isMuted ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM17.78 9.22a.75.75 0 10-1.061.06L18.44 12l-1.72 1.72a.75.75 0 001.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 101.06-1.06L20.56 12l1.72-1.72a.75.75 0 00-1.06-1.06l-1.72 1.72-1.72-1.72z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.584 5.106a.75.75 0 011.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 11-1.06-1.06 8.25 8.25 0 000-11.668.75.75 0 010-1.06z" />
                <path d="M15.932 7.757a.75.75 0 011.061 0 6 6 0 010 8.486.75.75 0 01-1.06-1.061 4.5 4.5 0 000-6.364.75.75 0 010-1.06z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer; 